import type { TilePosition } from '../../../model/gamestate/GameState';
import type { BiomeType } from '../../../model/gamestate/types';
import type { ActiveZoneData } from '../../../spacetime/modules/SkillModule';
import type { SkillTargetCandidate, SkillTargetingState } from '../../../events/SkillTargetingEvents';
import { getTerrainBiomeDefinition } from '../../../terrain/registry';
import {
  getPathTileQuarterAtlasDefinition,
  loadPathTileAtlasImage,
  type PathTileAtlasId,
} from '../../../terrain/pathTileRegistry';
import { pathAutotiler } from '../../../terrain/pathAutotiler/activePathAutotiler';
import { applyCanonicalPathTileEdit } from '../../../terrain/pathAutotiler/pathTileLayers';
import { encodeFixedPathVariantId } from '../../../terrain/pathAutotiler/pathManualVariant';
import { recommendPathTileAt } from '../../../terrain/pathAutotiler/recommendPathTile';
import {
  drawGridClippedPathTileImage,
  resolvePathTileDrawGeometry,
  resolvePathTileTransformCenter,
} from '../../tilemap/pathTileRenderGeometry';
import { resolveTileDrawMetrics } from '../../tilemap/board';
import { getQueueMarkerSprite } from '../../tilemap/runtimeSpriteCache';
import { COLORS } from '../../tilemap/constants';
import type { World2DCamera } from '../camera';
import type { GridGeometry } from '../../../grid';
import type { DynamicHitTarget } from './DynamicEntityLayer';
import type { MapDefinition } from '../../../model/gamestate/GameState';
import type { WorldAtlasAssetProvider } from '../assets/assetProvider';

interface MapObjectPlacementPreviewState {
  active: boolean;
  assetId: string | null;
  previewMode: 'tile-anchor' | 'map-anchor';
  tileX?: number;
  tileY?: number;
  mapX?: number;
  mapY?: number;
  offsetX?: number;
  offsetY?: number;
  sizeMultiplier: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
}

interface PathTilePlacementPreviewState {
  active: boolean;
  atlasId: PathTileAtlasId | null;
  tileIndex: number | null;
  autotile: boolean;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
}

interface BiomeRegionPlacementPreviewState {
  active: boolean;
  biome: BiomeType | null;
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
}

export class UiLayer {
  private readonly requestRender: () => void;
  private queuePreview: (TilePosition | null)[] = [];
  private activeZones: ActiveZoneData[] = [];
  private hoveredTile: TilePosition | null = null;
  private hoveredTileInFog = false;
  private skillTargetingActive = false;
  private skillTargetingHovered: SkillTargetCandidate | null = null;
  private skillTargetingSelected: Array<SkillTargetCandidate | null> = [];
  private hoveredActor: DynamicHitTarget | null = null;
  private hoverPointer: { x: number; y: number } | null = null;
  private mapObjectPlacementPreview: MapObjectPlacementPreviewState = {
    active: false,
    assetId: null,
    previewMode: 'tile-anchor',
    sizeMultiplier: 1,
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };
  private pathTilePlacementPreview: PathTilePlacementPreviewState = {
    active: false,
    atlasId: null,
    tileIndex: null,
    autotile: false,
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };
  private biomeRegionPlacementPreview: BiomeRegionPlacementPreviewState = {
    active: false,
    biome: null,
  };
  private readonly pathTileAtlasImages = new Map<string, HTMLImageElement>();
  private readonly pendingPathTileAtlasImages = new Map<string, Promise<HTMLImageElement>>();
  private readonly biomePreviewImages = new Map<BiomeType, HTMLImageElement>();

  constructor(requestRender: () => void) {
    this.requestRender = requestRender;
  }

  public setQueuePreview(queue: (TilePosition | null)[]): void {
    this.queuePreview = queue.slice();
  }

  public setActiveZones(zones: ActiveZoneData[]): void {
    this.activeZones = zones.slice();
  }

  public setHoveredTile(tile: TilePosition | null, inFog: boolean): void {
    this.hoveredTile = tile ? { ...tile } : null;
    this.hoveredTileInFog = inFog;
  }

  public setSkillTargetingActive(active: boolean): void {
    this.skillTargetingActive = active;
  }

  public setSkillTargetingState(state: SkillTargetingState | null): void {
    this.skillTargetingActive = Boolean(state?.active);
    this.skillTargetingHovered = state?.hovered ?? null;
    this.skillTargetingSelected = state?.selected?.slice() ?? [];
  }

  public setHoveredActor(actor: DynamicHitTarget | null, pointer: { x: number; y: number } | null): void {
    this.hoveredActor = actor;
    this.hoverPointer = pointer ? { ...pointer } : null;
  }

  public setMapObjectPlacementPreview(preview: MapObjectPlacementPreviewState): void {
    this.mapObjectPlacementPreview = { ...preview };
  }

  public setPathTilePlacementPreview(preview: PathTilePlacementPreviewState): void {
    this.pathTilePlacementPreview = { ...preview };
  }

  public setBiomeRegionPlacementPreview(preview: BiomeRegionPlacementPreviewState): void {
    this.biomeRegionPlacementPreview = { ...preview };
    const biome = preview.biome;
    if (!preview.active || !biome || biome === 'default' || this.biomePreviewImages.has(biome)) {
      return;
    }
    const previewPath = getTerrainBiomeDefinition(biome)?.previewPath;
    if (!previewPath) {
      return;
    }
    const image = new Image();
    image.decoding = 'async';
    image.src = previewPath;
    image.onload = () => {
      this.biomePreviewImages.set(biome, image);
      this.requestRender();
    };
  }

  public draw(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    map: MapDefinition,
    assets: WorldAtlasAssetProvider,
    selfPosition: TilePosition | null,
    activeRadius: number,
  ): void {
    this.drawBiomeRegionPlacementPreview(ctx, camera, grid, map);
    this.drawPathTilePlacementPreview(ctx, camera, grid, map);
    this.drawMapObjectPlacementPreview(ctx, camera, grid, assets);
    this.drawHoverHighlight(ctx, camera, grid);
    this.drawSkillTargeting(ctx, camera, grid);
    this.drawQueuePreview(ctx, camera, grid, selfPosition);
    this.drawActiveZones(ctx, camera, grid);
    this.drawHoveredActorTooltip(ctx);
  }

  private drawSkillTargeting(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
  ): void {
    if (!this.skillTargetingActive) {
      return;
    }

    for (let index = 0; index < this.skillTargetingSelected.length; index += 1) {
      const candidate = this.skillTargetingSelected[index];
      if (!candidate) {
        continue;
      }
      this.drawTargetCandidate(ctx, camera, grid, candidate, index + 1, false);
    }

    if (this.skillTargetingHovered && !this.isCandidateSelected(this.skillTargetingHovered)) {
      this.drawTargetCandidate(ctx, camera, grid, this.skillTargetingHovered, null, true);
    }
  }

  private drawHoverHighlight(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
  ): void {
    if (!this.hoveredTile) {
      return;
    }

    const { x, y } = this.hoveredTile;
    const center = camera.tileCenterToScreen(x, y, grid);
    const halfSize = camera.getPixelsPerWorldUnit() * 0.5;
    ctx.save();
    ctx.fillStyle = this.hoveredTileInFog
      ? 'rgba(255, 255, 255, 0.04)'
      : this.skillTargetingActive
        ? 'rgba(255, 221, 87, 0.12)'
        : COLORS.hoverFill;
    grid.fillTile(ctx, center.x, center.y, halfSize * 0.95, x, y);

    ctx.strokeStyle = this.hoveredTileInFog
      ? 'rgba(255, 255, 255, 0.25)'
      : this.skillTargetingActive
        ? 'rgba(255, 221, 87, 0.75)'
        : COLORS.hoverStroke;
    ctx.lineWidth = this.skillTargetingActive ? 3 : 2;
    grid.drawTilePath(ctx, center.x, center.y, halfSize * 0.95, x, y);
    ctx.stroke();
    ctx.restore();
  }

  private drawQueuePreview(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    selfPosition: TilePosition | null,
  ): void {
    if (!selfPosition || this.queuePreview.length === 0) {
      return;
    }

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = COLORS.pathLine;
    ctx.lineWidth = 4;
    ctx.setLineDash([10, 10]);
    const startCenter = camera.tileCenterToScreen(selfPosition.x, selfPosition.y, grid);
    ctx.moveTo(startCenter.x, startCenter.y);
    for (const target of this.queuePreview) {
      if (!target) {
        continue;
      }
      const targetCenter = camera.tileCenterToScreen(target.x, target.y, grid);
      ctx.lineTo(targetCenter.x, targetCenter.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    const occupancy: Record<string, number> = {};
    for (let index = 0; index < this.queuePreview.length; index += 1) {
      const target = this.queuePreview[index];
      if (!target) {
        continue;
      }
      const key = `${target.x},${target.y}`;
      const count = occupancy[key] || 0;
      occupancy[key] = count + 1;
      const center = camera.tileCenterToScreen(target.x, target.y, grid);
      const offsetX = count * 5 - 5;
      const offsetY = count * 5 - 5;
      const marker = getQueueMarkerSprite(String(index + 1));
      ctx.drawImage(
        marker.canvas,
        center.x + offsetX - marker.logicalWidth / 2,
        center.y + offsetY - marker.logicalHeight / 2,
        marker.logicalWidth,
        marker.logicalHeight,
      );
    }
    ctx.restore();
  }

  private drawActiveZones(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
  ): void {
    for (const zone of this.activeZones) {
      const center = camera.tileCenterToScreen(zone.centerX, zone.centerY, grid);
      const drawRadius = (zone.radius + 0.75) * camera.getPixelsPerWorldUnit();

      if (zone.zoneType === 'poison_fog' || zone.zoneType === 'poison_cloud') {
        const gradient = ctx.createRadialGradient(
          center.x,
          center.y,
          drawRadius * 0.25,
          center.x,
          center.y,
          drawRadius,
        );
        if (zone.zoneType === 'poison_cloud') {
          gradient.addColorStop(0, 'rgba(160, 215, 90, 0.23)');
          gradient.addColorStop(0.55, 'rgba(130, 185, 70, 0.16)');
          gradient.addColorStop(1, 'rgba(90, 145, 45, 0)');
        } else {
          gradient.addColorStop(0, 'rgba(120, 200, 70, 0.20)');
          gradient.addColorStop(0.55, 'rgba(90, 170, 55, 0.14)');
          gradient.addColorStop(1, 'rgba(70, 130, 40, 0)');
        }
        ctx.save();
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(center.x, center.y, drawRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        continue;
      }

      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(center.x, center.y, drawRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawHoveredActorTooltip(ctx: CanvasRenderingContext2D): void {
    if (!this.hoveredActor || !this.hoverPointer || typeof this.hoveredActor.hp !== 'number') {
      return;
    }

    const hpText = Math.round(this.hoveredActor.hp);
    const maxHpText = typeof this.hoveredActor.maxHp === 'number'
      ? ` / ${Math.round(this.hoveredActor.maxHp)}`
      : '';
    const staminaText = typeof this.hoveredActor.stamina === 'number'
      ? `  ST: ${Math.round(this.hoveredActor.stamina)}${typeof this.hoveredActor.maxStamina === 'number' ? ` / ${Math.round(this.hoveredActor.maxStamina)}` : ''}`
      : '';
    const text = `HP: ${hpText}${maxHpText}${staminaText}`;
    const x = this.hoverPointer.x + 12;
    const y = this.hoverPointer.y - 28;

    ctx.save();
    ctx.font = '12px Arial, sans-serif';
    const textWidth = ctx.measureText(text).width;
    const width = textWidth + 16;
    const height = 22;
    const canvasWidth = ctx.canvas.width / (window.devicePixelRatio || 1);
    const canvasHeight = ctx.canvas.height / (window.devicePixelRatio || 1);
    const drawX = Math.max(8, Math.min(x, canvasWidth - width - 8));
    const drawY = Math.max(8, Math.min(y, canvasHeight - height - 8));

    ctx.fillStyle = 'rgba(10, 14, 20, 0.92)';
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.7)';
    ctx.lineWidth = 1;
    ctx.fillRect(drawX, drawY, width, height);
    ctx.strokeRect(drawX, drawY, width, height);

    ctx.fillStyle = '#e5e7eb';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, drawX + 8, drawY + height / 2 + 0.5);
    ctx.restore();
  }

  private drawMapObjectPlacementPreview(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    assets: WorldAtlasAssetProvider,
  ): void {
    const preview = this.mapObjectPlacementPreview;
    if (!preview.active || !preview.assetId) {
      return;
    }
    const frame = assets.getObjectFrame(preview.assetId);
    if (!frame) {
      return;
    }
    const page = assets.getPage(frame.pageId);
    if (!page) {
      return;
    }

    const anchorTileX = preview.previewMode === 'map-anchor'
      ? preview.mapX
      : Number.isFinite(preview.tileX) ? preview.tileX : this.hoveredTile?.x;
    const anchorTileY = preview.previewMode === 'map-anchor'
      ? preview.mapY
      : Number.isFinite(preview.tileY) ? preview.tileY : this.hoveredTile?.y;
    if (!Number.isFinite(anchorTileX) || !Number.isFinite(anchorTileY)) {
      return;
    }

    const anchor = camera.tileCenterToScreen(anchorTileX!, anchorTileY!, grid);
    const pixelsPerWorldUnit = camera.getPixelsPerWorldUnit();
    const drawWidth = frame.drawWidth * preview.sizeMultiplier * pixelsPerWorldUnit;
    const drawHeight = frame.drawHeight * preview.sizeMultiplier * pixelsPerWorldUnit;
    const drawAnchorX = anchor.x + ((preview.offsetX ?? 0) * pixelsPerWorldUnit);
    const drawAnchorY = anchor.y + (((preview.offsetY ?? 0) + 0.5) * pixelsPerWorldUnit);

    ctx.save();
    ctx.globalAlpha = 0.38;
    ctx.translate(drawAnchorX, drawAnchorY);
    ctx.scale(preview.flipHorizontal ? -1 : 1, preview.flipVertical ? -1 : 1);
    ctx.rotate((preview.rotationSteps * Math.PI) / 2);
    ctx.drawImage(
      page.image,
      frame.pixelRect.x,
      frame.pixelRect.y,
      frame.pixelRect.width,
      frame.pixelRect.height,
      -drawWidth * frame.pivot.x,
      -drawHeight * frame.pivot.y,
      drawWidth,
      drawHeight,
    );
    ctx.restore();
  }

  private drawPathTilePlacementPreview(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    map: MapDefinition,
  ): void {
    const preview = this.pathTilePlacementPreview;
    if (!preview.active || !this.hoveredTile || !preview.atlasId) {
      return;
    }
    if (map.gridType !== 'square' && map.gridType !== 'hex' && map.gridType !== 'triangle') {
      return;
    }
    const atlas = getPathTileQuarterAtlasDefinition(preview.atlasId, map.gridType);
    if (!atlas) {
      return;
    }
    const image = this.getPathTileAtlasImage(preview.atlasId, map.gridType);
    const tile = this.hoveredTile;
    const manualVariantId = preview.tileIndex === null
      ? null
      : encodeFixedPathVariantId(map.gridType, preview.tileIndex);
    const previewRecommendation = preview.autotile
      ? recommendPathTileAt(map, preview.atlasId, tile.x, tile.y)
      : null;
    const previewCell = preview.autotile
      ? previewRecommendation
        ? {
            mode: 'autotile' as const,
            surfaceTerrain: map.tiles[tile.y]?.[tile.x]?.terrain ?? 'floor',
          }
        : null
      : manualVariantId
        ? {
            mode: 'fixed' as const,
            variantId: manualVariantId,
            surfaceTerrain: map.tiles[tile.y]?.[tile.x]?.terrain ?? 'floor',
            flipHorizontal: preview.flipHorizontal,
            flipVertical: preview.flipVertical,
            rotationSteps: preview.rotationSteps,
          }
        : null;
    if (!previewCell) {
      return;
    }
    const tileOffset = (tile.y * map.width) + tile.x;
    const previewMap = preview.autotile
      ? {
          ...map,
          pathLayers: applyCanonicalPathTileEdit(map.width, map.height, map.pathLayers, {
            atlasId: preview.atlasId,
            tileOffset,
            cell: previewCell,
          }, map.gridType),
        }
      : map;
    const renderTile = pathAutotiler.resolveRenderTile(
      previewMap,
      preview.atlasId,
      tile.x,
      tile.y,
      previewCell,
      atlas,
      image ?? null,
    );
    if (!renderTile) {
      return;
    }

    const tileMetrics = resolveTileDrawMetrics(grid, camera.getPixelsPerWorldUnit(), tile.x, tile.y);
    const geometry = resolvePathTileDrawGeometry(map.gridType, camera.getPixelsPerWorldUnit(), tileMetrics, atlas.drawScale ?? 1);
    const screenCenter = camera.worldPointToScreen({ x: geometry.boxCenterX, y: geometry.boxCenterY }, grid);
    const screenOrigin = camera.worldPointToScreen({
      x: geometry.boxCenterX - geometry.drawWidth / 2,
      y: geometry.boxCenterY - geometry.drawHeight / 2,
    }, grid);

    ctx.save();
    ctx.globalAlpha = 0.42;
    ctx.imageSmoothingEnabled = false;
    const previewRotationSteps = previewCell.mode === 'fixed' ? (previewCell.rotationSteps ?? 0) : 0;
    const previewFlipHorizontal = previewCell.mode === 'fixed' && previewCell.flipHorizontal === true;
    const previewFlipVertical = previewCell.mode === 'fixed' && previewCell.flipVertical === true;
    if (renderTile.kind === 'image') {
      const transformCenter = resolvePathTileTransformCenter(map.gridType, tileMetrics);
      if (map.gridType === 'hex' || map.gridType === 'triangle') {
        drawGridClippedPathTileImage({
          context: ctx,
          grid,
          gridType: map.gridType,
          tileSize: camera.getPixelsPerWorldUnit(),
          tileX: tile.x,
          tileY: tile.y,
          boxCenterX: screenCenter.x,
          boxCenterY: screenCenter.y,
          transformCenterX: renderTile.applyCellTransform && transformCenter
            ? camera.worldPointToScreen({ x: transformCenter.x, y: transformCenter.y }, grid).x
            : undefined,
          transformCenterY: renderTile.applyCellTransform && transformCenter
            ? camera.worldPointToScreen({ x: transformCenter.x, y: transformCenter.y }, grid).y
            : undefined,
          drawWidth: geometry.drawWidth,
          drawHeight: geometry.drawHeight,
          image: renderTile.image,
          rotationSteps: renderTile.applyCellTransform ? previewRotationSteps : 0,
          flipHorizontal: renderTile.applyCellTransform ? previewFlipHorizontal : false,
          flipVertical: renderTile.applyCellTransform ? previewFlipVertical : false,
        });
      } else if (renderTile.applyCellTransform) {
        ctx.translate(screenCenter.x, screenCenter.y);
        if (previewRotationSteps !== 0) {
          ctx.rotate(((((previewRotationSteps % 12) + 12) % 12) * (Math.PI / 6)));
        }
        ctx.scale(previewFlipHorizontal ? -1 : 1, previewFlipVertical ? -1 : 1);
        ctx.drawImage(
          renderTile.image,
          -geometry.drawWidth / 2,
          -geometry.drawHeight / 2,
          geometry.drawWidth,
          geometry.drawHeight,
        );
      } else {
        ctx.drawImage(renderTile.image, screenOrigin.x, screenOrigin.y, geometry.drawWidth, geometry.drawHeight);
      }
      ctx.restore();
      return;
    }

    if (!image) {
      ctx.restore();
      return;
    }
    const sourceX = (renderTile.tileIndex % atlas.columns) * atlas.tileSize;
    const sourceY = Math.floor(renderTile.tileIndex / atlas.columns) * atlas.tileSize;
    drawGridClippedPathTileImage({
      context: ctx,
      grid,
      gridType: map.gridType,
      tileSize: camera.getPixelsPerWorldUnit(),
      tileX: tile.x,
      tileY: tile.y,
      boxCenterX: screenCenter.x,
      boxCenterY: screenCenter.y,
      drawWidth: geometry.drawWidth,
      drawHeight: geometry.drawHeight,
      image,
      sourceX,
      sourceY,
      sourceWidth: atlas.tileSize,
      sourceHeight: atlas.tileSize,
      rotationSteps: renderTile.rotationSteps,
      flipHorizontal: renderTile.flipHorizontal,
      flipVertical: renderTile.flipVertical,
    });
    ctx.restore();
  }

  private drawBiomeRegionPlacementPreview(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    map: MapDefinition,
  ): void {
    const preview = this.biomeRegionPlacementPreview;
    if (!preview.active || !preview.biome) {
      return;
    }
    const { startX, startY, endX, endY } = preview;
    if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(endX) || !Number.isFinite(endY)) {
      return;
    }
    const minX = Math.min(startX!, endX!);
    const maxX = Math.max(startX!, endX!);
    const minY = Math.min(startY!, endY!);
    const maxY = Math.max(startY!, endY!);
    const image = preview.biome === 'default' ? null : this.biomePreviewImages.get(preview.biome) ?? null;
    const fallbackColor = preview.biome === 'water'
      ? 'rgba(59, 130, 246, 0.28)'
      : preview.biome === 'sand'
        ? 'rgba(245, 158, 11, 0.24)'
        : preview.biome === 'grass' || preview.biome === 'soft_grass'
          ? 'rgba(34, 197, 94, 0.24)'
          : 'rgba(148, 163, 184, 0.22)';

    ctx.save();
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        if (x < 0 || y < 0 || x >= map.width || y >= map.height) {
          continue;
        }
        const tileMetrics = resolveTileDrawMetrics(grid, camera.getPixelsPerWorldUnit(), x, y);
        const center = camera.worldPointToScreen({ x: tileMetrics.centerX, y: tileMetrics.centerY }, grid);
        const topLeft = camera.worldPointToScreen({ x: tileMetrics.originX, y: tileMetrics.originY }, grid);
        if (image) {
          ctx.save();
          grid.drawTilePath(ctx, center.x, center.y, camera.getPixelsPerWorldUnit() * 0.5, x, y);
          ctx.clip();
          ctx.globalAlpha = 0.42;
          ctx.drawImage(image, topLeft.x, topLeft.y, tileMetrics.spanWidth, tileMetrics.spanHeight);
          ctx.restore();
        } else {
          ctx.fillStyle = fallbackColor;
          grid.fillTile(ctx, center.x, center.y, camera.getPixelsPerWorldUnit() * 0.48, x, y);
        }

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
        ctx.lineWidth = 1;
        grid.drawTilePath(ctx, center.x, center.y, camera.getPixelsPerWorldUnit() * 0.48, x, y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawTargetCandidate(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    grid: GridGeometry,
    candidate: SkillTargetCandidate,
    slotNumber: number | null,
    preview: boolean,
  ): void {
    const center = camera.tileCenterToScreen(candidate.position.x, candidate.position.y, grid);
    const halfSize = camera.getPixelsPerWorldUnit() * 0.5;

    ctx.save();
    ctx.fillStyle = preview ? 'rgba(125, 211, 252, 0.10)' : 'rgba(250, 204, 21, 0.14)';
    grid.fillTile(ctx, center.x, center.y, halfSize * 0.9, candidate.position.x, candidate.position.y);

    ctx.strokeStyle = preview ? 'rgba(125, 211, 252, 0.85)' : 'rgba(250, 204, 21, 0.95)';
    ctx.lineWidth = preview ? 2 : 3;
    if (preview) {
      ctx.setLineDash([6, 4]);
    }
    grid.drawTilePath(ctx, center.x, center.y, halfSize * 0.9, candidate.position.x, candidate.position.y);
    ctx.stroke();
    ctx.setLineDash([]);

    if (slotNumber !== null) {
      ctx.fillStyle = 'rgba(17, 24, 39, 0.92)';
      ctx.beginPath();
      ctx.arc(center.x, center.y - halfSize * 0.45, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(250, 204, 21, 0.95)';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#f9fafb';
      ctx.font = 'bold 11px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(slotNumber), center.x, center.y - halfSize * 0.45 + 0.5);
    }
    ctx.restore();
  }

  private isCandidateSelected(candidate: SkillTargetCandidate): boolean {
    return this.skillTargetingSelected.some((selected) => {
      if (!selected) {
        return false;
      }
      return selected.targetType === candidate.targetType
        && selected.position.x === candidate.position.x
        && selected.position.y === candidate.position.y
        && selected.actorId === candidate.actorId;
    });
  }

  private getPathTileAtlasImage(atlasId: PathTileAtlasId, gridType: string): HTMLImageElement | null {
    const cacheKey = `${gridType}:${atlasId}`;
    const existing = this.pathTileAtlasImages.get(cacheKey) ?? null;
    if (existing || this.pendingPathTileAtlasImages.has(cacheKey)) {
      return existing;
    }
    const pending = loadPathTileAtlasImage(atlasId, gridType)
      .then((image) => {
        this.pendingPathTileAtlasImages.delete(cacheKey);
        this.pathTileAtlasImages.set(cacheKey, image);
        this.requestRender();
        return image;
      })
      .catch((error) => {
        this.pendingPathTileAtlasImages.delete(cacheKey);
        throw error;
      });
    this.pendingPathTileAtlasImages.set(cacheKey, pending);
    return null;
  }
}
